import { digger } from '@cbre/digger';
import { concatValuesBy, defaultTxt } from '@miq/utils';
import { map } from 'rxjs/operators';
import { Layout } from '../../../../models/blueprint.model';
import { ReportRecord } from '../../../../models/report.model';
import { LovValues, TemplateFieldsDisplayFn } from '../../../../models/template.model';
import {
  annualRentAndOutgoingFormatter,
  fitOutFieldValueFormatter,
  getMediaInformation,
} from '../../templates-formatter.const';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../utils/dynamic-data-retrievers';
import { AvailabilityTypesLabels } from '../common';
import { Country } from '../fields-args.const';
import { currencyLov } from '../us/us.common';
import { getFooterLabel } from './pacific-availability.common';

const minRowHeight = 40;
const mainPageTableSize = minRowHeight * 10;
const additionalPageTableSize = minRowHeight * 27;
const greenRating = 'Green';
const naberRating = 'Nabers';
const nbs = 'NBS';
const seismicRating = 'SEISMIC';
const naberRatingValDec = 'NABERS Energy rating';

export const displayFnMap: TemplateFieldsDisplayFn = {
  574: { displayFn: displayFnWrapper }, // Outgoing Comments
  199: {
    displayFn: (record: ReportRecord, _, arg) => {
      return mapAndFormatDynamicValue(record, arg, null, null, false);
    },
  }, // Lease Type
  538: {
    // Subheader
    displayFn: (record: ReportRecord, options, suburb) => {
      const suburbVal = mapAndFormatDynamicValue(record, suburb);
      return options.isPPTHasSpecificFormat && suburbVal === defaultTxt ? ' ' : suburbVal;
      return suburbVal;
    },
  },
  224: { displayFn: displayFnWrapper }, // Property Information
  225: {
    displayFn: (record: ReportRecord, _, country) => {
      const ratings = digger(record.associatedProperty, 'property_external_ratings');
      const countryVal = mapAndFormatDynamicValue(record, country);
      let result;
      if (countryVal !== Country.NewZealand) {
        const nabers = (ratings || []).find(
          rating =>
            !!rating.green_building_rating_type_desc &&
            rating.green_building_rating_type_desc.toLowerCase().trim() === naberRating.toLowerCase().trim()
        );
        result =
          nabers && nabers.green_building_cert_level_desc
            ? `${nabers.green_building_cert_level_desc} ${naberRatingValDec}`
            : defaultTxt;
      } else {
        const nbsValue = (ratings || []).find(
          rating =>
            !!rating.green_building_rating_type_desc &&
            rating.green_building_rating_type_desc.toLowerCase().trim() === nbs.toLowerCase().trim()
        );
        result =
          nbsValue && nbsValue.green_building_cert_level_desc
            ? `${nbsValue.green_building_cert_level_desc} ${nbs}`
            : defaultTxt;
      }
      return result;
    },
    labelDisplayFn: (record: ReportRecord, _, country) => {
      const countryVal = mapAndFormatDynamicValue(record, country);
      const countryBasedDesc = countryVal === Country.NewZealand ? seismicRating : naberRating;
      return countryBasedDesc;
    },
    isVisible: (record: ReportRecord, settings) => {
      const country = settings.field.displayFnArguments[0];
      const countryVal = mapAndFormatDynamicValue(record, country);
      return countryVal !== Country.NewZealand;
    },
  }, // External Rating
  181: {
    displayFn: (record: ReportRecord, _, arg) => {
      const columnData = mapAndFormatDynamicValue(record, arg, null, null, false);
      const result = ((columnData as string[]) || []).map((comment, index) => {
        if (record.layout) {
          comment = comment.trim().replace(/\r?\n|\r/g, ' ');
          let restrictLength =
            record.layout === Layout.MainLayout && index == 0 ? mainPageTableSize : additionalPageTableSize;
          if (comment && comment.length > restrictLength) {
            comment = comment.substring(0, restrictLength - 3) + '...';
          }
        }
        return comment;
      });
      return result;
    },
  }, // Comments
  227: {
    displayFn: (record: ReportRecord, _, suite, floorNumber) => {
      const suiteVal = mapAndFormatDynamicValue(record, suite, null, null, false);
      const floorNumberVal = mapAndFormatDynamicValue(record, floorNumber, null, null, false);
      const result = (suiteVal as string[]).map((val, i) => concatValuesBy('/', floorNumberVal[i], val));
      return result;
    },
  }, // Level/Suite
  228: {
    displayFn: (record: ReportRecord, _, arg) => {
      return mapAndFormatDynamicValue(record, arg, null, null, false);
    },
  }, // Area sqm
  229: {
    displayFn: (record: ReportRecord, options, rent) => {
      return currencyLov(options).pipe(
        map((currencyLOV: LovValues[]) => {
          const nzcurrency = currencyLOV.map(currency => {
            if (currency.key === 'New Zealand Dollar') {
              currency.value = '$';
            }
            return currency;
          });
          const uomArgument = { ...rent.uomArgument, isPrefix: true };
          rent = { ...rent, uomArgument };
          return mapAndFormatDynamicValue(record, rent, nzcurrency, true, false);
        })
      );
    },
  }, // Rent psm/pa ex GST
  230: {
    displayFn: (record: ReportRecord, options, outGoings) => {
      return currencyLov(options).pipe(
        map((currencyLOV: LovValues[]) => {
          const nzcurrency = currencyLOV.map(currency => {
            if (currency.key === 'New Zealand Dollar') {
              currency.value = '$';
            }
            return currency;
          });
          const uomArgument = { ...outGoings.uomArgument, isPrefix: true };
          outGoings = { ...outGoings, uomArgument };
          return mapAndFormatDynamicValue(record, outGoings, nzcurrency, true, false);
        })
      );
    },
  }, // Outgoings ex GST
  231: {
    displayFn: (record: ReportRecord, _, avilableSpace, askingRent, outgoings) => {
      const availabelSpaceval = mapAndFormatDynamicValue(record, avilableSpace, null, false, false) as string[];
      const askingRentval = mapAndFormatDynamicValue(record, askingRent, null, false, false) as string[];
      const outGoingsSpaceVal = mapAndFormatDynamicValue(record, outgoings, null, false, false) as string[];
      return annualRentAndOutgoingFormatter(askingRentval, availabelSpaceval, outGoingsSpaceVal, outgoings);
    },
  }, // Annual Rent + Outgoings ex GST
  232: {
    displayFn: (record: ReportRecord, _, arg) => {
      const fitOutVal = mapAndFormatDynamicValue(record, arg, null, null, false) as string[];
      return fitOutFieldValueFormatter(fitOutVal);
    },
  }, // Fit Out
  49: { displayFn: displayFnWrapper }, // Property Name
  552: {
    displayFn: (record: ReportRecord, _, propertyName, streetName) => {
      return mapAndFormatDynamicValue(record, streetName);
    },
    routeFn: ({ associatedProperty }) => {
      return ['/records', 'property', digger(associatedProperty, 'entity.id'), 'details'];
    },
    isVisible: (record: ReportRecord, settings) => {
      const propertyName = settings.field.displayFnArguments[0];
      const streetName = settings.field.displayFnArguments[1];
      let propertyNameVal = mapAndFormatDynamicValue(record, propertyName);
      let addressVal = mapAndFormatDynamicValue(record, streetName);
      if (propertyNameVal && addressVal) {
        const properyArray: Array<string> = (<string>propertyNameVal).split(' ').filter(Boolean);
        const addressArray: Array<string> = (<string>addressVal).split(' ').filter(Boolean);
        if (properyArray.length !== addressArray.length) {
          return true;
        }
        properyArray.pop();
        addressArray.pop();
        propertyNameVal = properyArray.join(' ');
        addressVal = addressArray.join(' ');
        return propertyNameVal !== addressVal;
      }
    },
  }, // Property Address
  50: { displayFn: displayFnWrapper }, // Location
  223: { displayFn: displayFnWrapper }, // Contacts
  242: { displayFn: displayFnWrapper }, // Location Information
  98: {
    displayFn: (record: ReportRecord) => {
      return getMediaInformation(record);
    },
  }, // Primary Image
  99: {
    displayFn: (record: ReportRecord) => {
      return getMediaInformation(record);
    },
  }, // Additional Image
  254: {
    displayFn: (record: ReportRecord) => {
      return getMediaInformation(record);
    },
  }, // Secondary Image
  259: {
    displayFn: (record: ReportRecord, _, availableType) => {
      const availableTypeVal = mapAndFormatDynamicValue(record, availableType) as string;
      const result = !!AvailabilityTypesLabels[availableTypeVal] ? AvailabilityTypesLabels[availableTypeVal] : '-';
      return result;
    },
  }, // title based on availability Type
  347: {
    displayFn: (record: ReportRecord) => {
      return getMediaInformation(record);
    },
  },
  553: {
    displayFn: (record: ReportRecord, _, suite, floorNumber) => {
      const suiteVal = mapAndFormatDynamicValue(record, suite, null, null, false);
      const floorNumberVal = mapAndFormatDynamicValue(record, floorNumber, null, null, false);
      const floorPlanConst = ['Floorplan: ', 'Floor: ', ', Suite: '];
      const suitValArr: string[] = !Array.isArray(suiteVal) ? [suiteVal] : suiteVal;

      return (
        floorPlanConst[0] +
        suitValArr.map((val, i) => floorPlanConst[1] + concatValuesBy(floorPlanConst[2], floorNumberVal[i], val))
      );
    },
  }, // Floor/Suite
  350: { displayFn: displayFnWrapper }, // Car Parking Comments
  ...getFooterLabel(),
};
